import AuthService from './services/auth.js';
import { showNotification } from './utils.js';
import { CompaniesView, TemplatesView, MessageStatisticsView } from './views/companies/index.js';
import ProfileView from './views/profile.js';
import { VerificationCodeInput } from './views/auth/VerificationCodeInput.js';

class App {
    constructor() {
        this.authService = AuthService;
        this.currentView = null;
        this.views = {
            companies: new CompaniesView(),
            profile: new ProfileView(),
            templates: new TemplatesView()
        };

        // Wait for DOM to be fully loaded before initializing
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.initialize());
        } else {
            this.initialize();
        }
    }

    initialize() {
        this.initializeAuth();
        this.attachEventListeners();
    }

    initializeAuth() {
        if (this.authService.isAuthenticated()) {
            this.showApp();
            this.loadInitialView();
        } else {
            this.showAuth();
        }
    }

    attachEventListeners() {
        // Auth form toggling
        const showRegisterBtn = document.getElementById('show-register');
        const showLoginBtn = document.getElementById('show-login');
        const loginForm = document.getElementById('login-form');
        const registerForm = document.getElementById('register-form');

        showRegisterBtn?.addEventListener('click', () => {
            loginForm?.classList.add('hidden');
            registerForm?.classList.remove('hidden');
        });

        showLoginBtn?.addEventListener('click', () => {
            registerForm?.classList.add('hidden');
            loginForm?.classList.remove('hidden');
        });

        // Login form
        document.getElementById('login-form-element')?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const email = document.getElementById('login-email').value;
            const password = document.getElementById('login-password').value;

            try {
                await this.authService.login(email, password);
                this.showApp();
                this.loadInitialView();
            } catch (error) {
                showNotification(error.message, 'error');
            }
        });

        // Register form step 1
        document.getElementById('register-form-element')?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const email = document.getElementById('register-email').value;
            const username = document.getElementById('register-username').value;
            const phone = document.getElementById('register-phone').value;

            try {
                await this.authService.initiateRegistration(email, username, phone);
                // Hide registration form and show verification form container
                document.getElementById('register-form').classList.add('hidden');
                document.getElementById('verification-form').classList.remove('hidden');
                showNotification('Verification code sent to your email.');
                
                // Instantiate VerificationCodeInput component
                const verificationContainer = document.getElementById('verification-container');
                if (verificationContainer) {
                    verificationContainer.innerHTML = '';
                    const verificationComponent = new VerificationCodeInput(email, (code) => {
                        document.getElementById('verification-form').classList.add('hidden');
                        document.getElementById('complete-registration-form').classList.remove('hidden');
                        showNotification('Email verified successfully.');
                    });
                    verificationComponent.mount(verificationContainer);
                }
            } catch (error) {
                showNotification(error.message, 'error');
            }
        });

        // Complete registration form
        document.getElementById('complete-registration-form-element')?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const email = document.getElementById('register-email').value;
            const code = document.getElementById('verification-code').value;
            const password = document.getElementById('complete-password').value;
            const username = document.getElementById('register-username').value;
            const phone = document.getElementById('register-phone').value;

            try {
                await this.authService.completeRegistration(email, code, password, username, phone);
                showNotification('Registration successful! Please log in.');
                // Hide all registration forms and show login form
                document.getElementById('complete-registration-form').classList.add('hidden');
                document.getElementById('login-form').classList.remove('hidden');
            } catch (error) {
                showNotification(error.message, 'error');
            }
        });

        // Logout button
        document.getElementById('logout-btn')?.addEventListener('click', () => {
            this.authService.logout();
            this.showAuth();
        });

        // Profile Menu Toggle - Fixed Implementation
        this.setupProfileMenu();

        // Handle hash-based routing
        window.addEventListener('hashchange', () => {
            this.handleRoute();
        });

        // Navigation links
        document.addEventListener('click', (e) => {
            const navLink = e.target.closest('.nav-link');
            if (navLink) {
                e.preventDefault();
                const view = navLink.dataset.view;
                if (view) {
                    this.navigateTo(view);
                    
                    // Close user menu if open
                    const userMenu = document.getElementById('user-menu');
                    if (userMenu) {
                        userMenu.classList.add('hidden');
                    }

                    // Update active state
                    document.querySelectorAll('.nav-link').forEach(link => {
                        link.classList.toggle('active', link.dataset.view === view);
                    });
                }
            }
        });

        // Handle mobile menu
        const mobileMenuButton = document.getElementById('mobile-menu-button');
        const mobileMenu = document.getElementById('mobile-menu');
        
        if (mobileMenuButton && mobileMenu) {
            mobileMenuButton.addEventListener('click', () => {
                mobileMenu.classList.toggle('hidden');
            });
        }
    }

    setupProfileMenu() {
        const userMenuButton = document.getElementById('user-menu-button');
        const userMenu = document.getElementById('user-menu');

        if (userMenuButton && userMenu) {
            // Toggle menu on button click
            userMenuButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Toggle aria-expanded attribute
                const isExpanded = userMenuButton.getAttribute('aria-expanded') === 'true';
                userMenuButton.setAttribute('aria-expanded', !isExpanded);
                
                // Toggle menu visibility
                userMenu.classList.toggle('hidden');
            });

            // Close menu when clicking outside
            document.addEventListener('click', (e) => {
                if (!userMenuButton.contains(e.target) && !userMenu.contains(e.target)) {
                    userMenu.classList.add('hidden');
                    userMenuButton.setAttribute('aria-expanded', 'false');
                }
            });

            // Handle keyboard navigation
            userMenuButton.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    userMenu.classList.toggle('hidden');
                }
            });

            // Close menu on ESC key
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape' && !userMenu.classList.contains('hidden')) {
                    userMenu.classList.add('hidden');
                    userMenuButton.setAttribute('aria-expanded', 'false');
                }
            });
        }
    }

    handleRoute() {
        const hash = window.location.hash;
        if (hash.startsWith('#/companies/')) {
            const templatesMatch = hash.match(/^#\/companies\/(\d+)\/templates$/);
            if (templatesMatch) {
                const companyId = templatesMatch[1];
                this.navigateTo('templates', { companyId });
                return;
            }

            const statsMatch = hash.match(/^#\/companies\/(\d+)\/statistics$/);
            if (statsMatch) {
                const companyId = statsMatch[1];
                this.navigateTo('statistics', { companyId });
                return;
            }
        }
        
        // Default routes
        switch (hash) {
            case '#/companies':
                this.navigateTo('companies');
                break;
            case '#/profile':
                this.navigateTo('profile');
                break;
            default:
                if (!hash) {
                    this.navigateTo('companies');
                }
        }
    }

    showAuth() {
        const authContainer = document.getElementById('auth-container');
        const appContainer = document.getElementById('app-container');
        const loginForm = document.getElementById('login-form-element');
        const registerForm = document.getElementById('register-form-element');

        if (authContainer && appContainer) {
            authContainer.classList.remove('hidden');
            appContainer.classList.add('hidden');
            
            // Reset forms
            loginForm?.reset();
            registerForm?.reset();
        }
    }

    showApp() {
        const authContainer = document.getElementById('auth-container');
        const appContainer = document.getElementById('app-container');

        if (authContainer && appContainer) {
            authContainer.classList.add('hidden');
            appContainer.classList.remove('hidden');
        }
    }

    loadInitialView() {
        this.handleRoute();
    }

    async navigateTo(viewName, params) {
        if (viewName === 'statistics' && params?.companyId) {
            const view = new MessageStatisticsView(params.companyId);
            await view.render();
            const mainContent = document.getElementById('main-content');
            if (mainContent) {
                mainContent.innerHTML = '';
                mainContent.appendChild(view.getContainer());
            }
            return;
        }

        const view = this.views[viewName];
        if (view) {
            this.currentView = view;
            if (viewName === 'templates' && params?.companyId) {
                await view.mount(params.companyId);
            } else {
                await view.mount();
            }
        } else {
            showNotification('View not found', 'error');
        }
    }
}

// Initialize the application
window.addEventListener('DOMContentLoaded', () => {
    window.app = new App();
});

export default App;
